import moment from 'moment';
import React, { Component } from "react";
import 'react-block-ui/style.css';
import { FormControl, FormGroup, Panel } from 'react-bootstrap';
import Datetime from 'react-datetime';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import LoadingIndicator from '../Components/LoadingIndicator/LoadingIndicator';
import ReportsService from '../utils/ReportsService';
import UsersService from '../utils/UsersService';

class ReportingConfirmationCallScores extends Component {
  constructor(props) {
    super(props);
    const defaultFilter = {
      user_email: '',
      start_date: moment().subtract(30, 'days').format("DD MMM YYYY"),
      end_date: moment().format("DD MMM YYYY")
    }
    this.state = {
      filter: defaultFilter,
      data: null,
      users: [],
      isLoading: false
    };

    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.dateFilterChanged = this.dateFilterChanged.bind(this);
  }

  componentDidMount() {
    this.loadUsers();
    this.loadData();
  }

  loadUsers() {
    UsersService.allUsers()
      .then(data => {
        const coaches = data
          .filter(c => c.roles.includes("tcs_coach"))
          .sort((a, b) => (a.name > b.name ? 1 : -1));
        this.setState({ users: coaches });
      })
      .catch(error => {
        console.error('Error loading users:', error);
        this.setState({ users: [] });
      });
  }

  loadData() {
    this.setState({ isLoading: true });
    ReportsService.confirmationCallScores({ filter: this.state.filter })
      .then(response => {
        this.setState({ data: response, isLoading: false });
      })
      .catch(error => {
        console.error('Error loading confirmation call scores:', error);
        this.setState({ isLoading: false });
      });
  }

  handleFilterChange(event) {
    const { filter } = this.state;
    filter[event.target.name] = event.target.value;
    this.setState({ filter }, () => {
      this.loadData();
    });
  }

  dateFilterChanged(name, value) {
    const { filter } = this.state;
    filter[name] = moment(value).format("DD MMM YYYY");
    this.setState({ filter }, () => {
      this.loadData();
    });
  }

  render() {
    const { data, filter, users, isLoading } = this.state;

    if (!data) return <LoadingIndicator />;

    // Prepare chart data from daily_scores
    const chartData = Object.keys(data.daily_scores || {})
      .sort()
      .map(date => ({
        date: moment(date).format('MMM DD'),
        score: data.daily_scores[date]
      }));

    return (
      <div>
        <Panel bsStyle="info">
          <Panel.Heading>
            <Panel.Title componentClass="h3">Filters</Panel.Title>
          </Panel.Heading>
          <Panel.Body>
            <div className="row">
              <div className="col-md-3">
                <FormGroup>
                  <label>User</label>
                  <FormControl
                    componentClass="select"
                    name="user_email"
                    value={filter.user_email}
                    onChange={this.handleFilterChange}
                  >
                    <option value="">All Users</option>
                    {(users || []).map(user => (
                      <option key={user.email} value={user.email}>
                        {user.name}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </div>
              <div className="col-md-3">
                <FormGroup>
                  <label>Start Date</label>
                  <Datetime
                    timeFormat={false}
                    value={filter.start_date}
                    onChange={value => this.dateFilterChanged('start_date', value)}
                  />
                </FormGroup>
              </div>
              <div className="col-md-3">
                <FormGroup>
                  <label>End Date</label>
                  <Datetime
                    timeFormat={false}
                    value={filter.end_date}
                    onChange={value => this.dateFilterChanged('end_date', value)}
                  />
                </FormGroup>
              </div>
            </div>
          </Panel.Body>
        </Panel>

        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <div>
            {/* Summary Cards */}
            <div className="row" style={{ marginBottom: 20 }}>
              <div className="col-md-3">
                <div style={{
                  padding: 20,
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  textAlign: 'center'
                }}>
                  <h4 style={{ margin: 0, color: '#6c757d' }}>Total Calls</h4>
                  <h2 style={{ margin: '10px 0', color: '#007bff' }}>{data.total_calls}</h2>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  padding: 20,
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  textAlign: 'center'
                }}>
                  <h4 style={{ margin: 0, color: '#6c757d' }}>Overall Average</h4>
                  <h2 style={{ margin: '10px 0', color: '#28a745' }}>{data.avg_score}</h2>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  padding: 20,
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  textAlign: 'center'
                }}>
                  <h4 style={{ margin: 0, color: '#6c757d' }}>7-Day Average</h4>
                  <h2 style={{ margin: '10px 0', color: '#17a2b8' }}>{data.seven_day_avg}</h2>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  padding: 20,
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  textAlign: 'center'
                }}>
                  <h4 style={{ margin: 0, color: '#6c757d' }}>30-Day Average</h4>
                  <h2 style={{ margin: '10px 0', color: '#ffc107' }}>{data.thirty_day_avg}</h2>
                </div>
              </div>
            </div>

            {/* Score Trend Chart */}
            <Panel bsStyle="success">
              <Panel.Heading>
                <Panel.Title componentClass="h3">Score Trend Over Time</Panel.Title>
              </Panel.Heading>
              <Panel.Body>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#007bff"
                        strokeWidth={2}
                        name="Average Score"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p>No data available for the selected date range.</p>
                )}
              </Panel.Body>
            </Panel>

            {/* Score Distribution */}
            <Panel bsStyle="info">
              <Panel.Heading>
                <Panel.Title componentClass="h3">Score Distribution</Panel.Title>
              </Panel.Heading>
              <Panel.Body>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Score Range</th>
                      <th>Count</th>
                      <th>Percentage</th>
                      <th>Visual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(data.score_distribution || {}).map(range => {
                      const count = data.score_distribution[range];
                      const percentage = data.total_calls > 0 ? ((count / data.total_calls) * 100).toFixed(1) : 0;
                      return (
                        <tr key={range}>
                          <td>{range}</td>
                          <td>{count}</td>
                          <td>{percentage}%</td>
                          <td>
                            <div style={{
                              height: 24,
                              width: `${percentage}%`,
                              background: '#007bff',
                              minWidth: count > 0 ? 20 : 0
                            }}></div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Panel.Body>
            </Panel>

            {/* User Performance Table */}
            <Panel bsStyle="primary">
              <Panel.Heading>
                <Panel.Title componentClass="h3">User Performance</Panel.Title>
              </Panel.Heading>
              <Panel.Body>
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Total Calls</th>
                      <th>Average Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.user_stats || []).map((user, index) => (
                      <tr key={index}>
                        <td>{user.email}</td>
                        <td>{user.call_count}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 4,
                            background: user.avg_score >= 70 ? '#28a745' : user.avg_score >= 50 ? '#ffc107' : '#dc3545',
                            color: 'white',
                            fontWeight: 'bold'
                          }}>
                            {user.avg_score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel.Body>
            </Panel>
          </div>
        )}
      </div>
    );
  }
}

export default ReportingConfirmationCallScores;
